import type { InboxItem, PertoTask } from '@secretario/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { listPertoTasks, pertoTaskReply, PERTO_TASKS_KEY } from '../../lib/api/pertoTasks.js';
import { inboxActive, inboxSupported, listInbox, watchInbox } from '../../lib/inbox.js';
import {
  addReplyCandidate,
  inboxText,
  isOwnAsk,
  loadContactNames,
  looksLikeReply,
  PERTO_RECHECK_EVENT,
  placeMatches,
  setOpenPertoTasks,
  waitingPlaces,
} from '../../lib/pertoReplies.js';

const OPEN = new Set(['pedindo', 'esperando', 'escolhendo']);
const SEEN_KEY = 'secretario.perto.respostas';

const seen = (): Set<string> => {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SEEN_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
};
const remember = (key: string) => {
  try {
    const all = [...seen(), key].slice(-200);
    sessionStorage.setItem(SEEN_KEY, JSON.stringify(all));
  } catch {
    /* sem memória da sessão: no pior caso a resposta chega duas vezes, e o servidor guarda a última */
  }
};

/**
 * A resposta do negócio volta sozinha (PA2, só no Android, com o acesso a notificações ligado):
 * o aviso de um negócio de um pedido aberto, chegado depois do pedido, vira a resposta daquele
 * pedido. Reconhece pelo número, pelo nome ou pelo nome salvo na agenda; sem isso, uma mensagem
 * com cara de resposta vira pergunta na página do pedido. O texto é dado, nunca ordem.
 */
export function PertoReplyWatch({ enabled }: { enabled: boolean }) {
  const queryClient = useQueryClient();
  const on = enabled && inboxSupported();
  const tasksQ = useQuery({
    queryKey: PERTO_TASKS_KEY,
    queryFn: listPertoTasks,
    enabled: on,
    staleTime: 60_000,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((task) => OPEN.has(task.state)) ? 120_000 : false,
  });
  const tasks = useRef<PertoTask[]>([]);
  tasks.current = (tasksQ.data ?? []).filter((task) => OPEN.has(task.state));
  setOpenPertoTasks(tasks.current);
  const anyOpen = tasks.current.length > 0;

  useEffect(() => {
    if (!on || !anyOpen) return;
    let stop: (() => void) | null = null;
    let alive = true;
    const handle = (item: InboxItem) => {
      // A própria fala e as de grupo nunca são a resposta de um negócio.
      if (item.mine || item.from.includes(' · ')) return;
      for (const task of tasks.current) {
        const waiting = waitingPlaces(task, item.at);
        if (waiting.length === 0) continue;
        const text = inboxText(item);
        if (isOwnAsk(text, task)) continue;
        const place = waiting.find((one) => placeMatches(item.from, one));
        if (!place) {
          if (looksLikeReply(text))
            addReplyCandidate({
              key: `${item.from}|${item.at}`,
              taskId: task.id,
              from: item.from,
              text,
              at: item.at,
            });
          continue;
        }
        const key = `${task.id}:${place.placeId}`;
        if (seen().has(key)) continue;
        remember(key);
        void pertoTaskReply(task.id, place.placeId, text, item.at)
          .then(() => queryClient.invalidateQueries({ queryKey: PERTO_TASKS_KEY }))
          .catch(() => undefined);
      }
    };
    const scan = async () => {
      await loadContactNames();
      for (const item of await listInbox()) handle(item);
    };
    const recheck = () => {
      void loadContactNames(true).then(scan);
    };
    window.addEventListener(PERTO_RECHECK_EVENT, recheck);
    void (async () => {
      if (!(await inboxActive()) || !alive) return;
      // O que já estava na barra quando o app abriu, e o que chegar daqui em diante.
      await scan();
      stop = await watchInbox(handle);
      if (!alive) stop();
    })();
    return () => {
      alive = false;
      window.removeEventListener(PERTO_RECHECK_EVENT, recheck);
      stop?.();
    };
  }, [on, anyOpen, queryClient, tasksQ.data]);

  return null;
}
